import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/layout/MainLayout';
import {
  websiteSalesReportService,
  WebsiteSalesReportConfig,
  StoreType,
} from '../../api/services/websiteSalesReportService';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, Play, Clock, Globe, Mail, X, Check, ShoppingCart, Store } from 'lucide-react';

const STORE_TYPES: { value: StoreType; label: string; icon: string }[] = [
  { value: 'woocommerce', label: 'WooCommerce', icon: 'WC' },
  { value: 'magento2', label: 'Magento 2', icon: 'M2' },
  { value: 'custom', label: 'Custom API', icon: 'API' },
];

const WebsiteSalesReports: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<WebsiteSalesReportConfig | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    websiteUrl: '',
    storeType: 'woocommerce' as StoreType,
    wooConsumerKey: '',
    wooConsumerSecret: '',
    magentoConsumerKey: '',
    magentoConsumerSecret: '',
    magentoAccessToken: '',
    magentoAccessTokenSecret: '',
    magentoStoreId: '',
    customApiEndpoint: '',
    customApiKey: '',
    scheduleTimes: ['08:00'],
    recipients: [''],
    isActive: true,
  });

  // Fetch all configurations
  const { data: configs = [], isLoading, error } = useQuery({
    queryKey: ['websiteSalesReportConfigs'],
    queryFn: websiteSalesReportService.getAllConfigs,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: websiteSalesReportService.createConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSalesReportConfigs'] });
      toast.success('Configuration created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create configuration');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      websiteSalesReportService.updateConfig(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSalesReportConfigs'] });
      toast.success('Configuration updated successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update configuration');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: websiteSalesReportService.deleteConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSalesReportConfigs'] });
      toast.success('Configuration deleted successfully');
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete configuration');
    },
  });

  // Run report mutation
  const runReportMutation = useMutation({
    mutationFn: websiteSalesReportService.runReport,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['websiteSalesReportConfigs'] });
      toast.success(data.message || 'Report sent successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to run report');
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: websiteSalesReportService.testConnection,
    onSuccess: (data) => {
      toast.success(data.message || 'Connection successful');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Connection failed');
    },
  });

  const openCreateModal = () => {
    setEditingConfig(null);
    setFormData({
      name: '',
      websiteUrl: '',
      storeType: 'woocommerce',
      wooConsumerKey: '',
      wooConsumerSecret: '',
      magentoConsumerKey: '',
      magentoConsumerSecret: '',
      magentoAccessToken: '',
      magentoAccessTokenSecret: '',
      magentoStoreId: '',
      customApiEndpoint: '',
      customApiKey: '',
      scheduleTimes: ['08:00'],
      recipients: [''],
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (config: WebsiteSalesReportConfig) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      websiteUrl: config.websiteUrl,
      storeType: config.storeType || 'custom',
      wooConsumerKey: config.wooConsumerKey || '',
      wooConsumerSecret: config.wooConsumerSecret || '',
      magentoConsumerKey: config.magentoConsumerKey || '',
      magentoConsumerSecret: config.magentoConsumerSecret || '',
      magentoAccessToken: config.magentoAccessToken || '',
      magentoAccessTokenSecret: config.magentoAccessTokenSecret || '',
      magentoStoreId: config.magentoStoreId || '',
      customApiEndpoint: config.customApiEndpoint || '',
      customApiKey: config.customApiKey || '',
      scheduleTimes: config.scheduleTimes?.length > 0 ? config.scheduleTimes : ['08:00'],
      recipients: config.recipients.length > 0 ? config.recipients : [''],
      isActive: config.isActive,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingConfig(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validRecipients = formData.recipients.filter((email) => email.trim() !== '');

    if (validRecipients.length === 0) {
      toast.error('At least one recipient email is required');
      return;
    }

    const data = {
      ...formData,
      recipients: validRecipients,
    };

    if (editingConfig?.id) {
      updateMutation.mutate({ id: editingConfig.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleTestConnection = () => {
    testConnectionMutation.mutate({
      storeType: formData.storeType,
      websiteUrl: formData.websiteUrl,
      wooConsumerKey: formData.wooConsumerKey,
      wooConsumerSecret: formData.wooConsumerSecret,
      magentoConsumerKey: formData.magentoConsumerKey,
      magentoConsumerSecret: formData.magentoConsumerSecret,
      magentoAccessToken: formData.magentoAccessToken,
      magentoAccessTokenSecret: formData.magentoAccessTokenSecret,
      magentoStoreId: formData.magentoStoreId,
      customApiEndpoint: formData.customApiEndpoint,
      customApiKey: formData.customApiKey,
    });
  };

  const addRecipient = () => {
    setFormData((prev) => ({
      ...prev,
      recipients: [...prev.recipients, ''],
    }));
  };

  const removeRecipient = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index),
    }));
  };

  const updateRecipient = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.map((email, i) => (i === index ? value : email)),
    }));
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStoreTypeLabel = (storeType: StoreType) => {
    const type = STORE_TYPES.find((t) => t.value === storeType);
    return type?.label || 'Unknown';
  };

  const getStoreTypeIcon = (storeType: StoreType) => {
    switch (storeType) {
      case 'woocommerce':
        return <ShoppingCart className="h-4 w-4" />;
      case 'magento2':
        return <Store className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Failed to load configurations</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Website Sales Reports
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Configure automated daily sales reports from WooCommerce, Magento 2, or custom API stores
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Store
          </button>
        </div>

        {/* Configurations List */}
        {configs.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
            <Store className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No stores configured</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding a WooCommerce or Magento 2 store to receive daily sales reports.
            </p>
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Store
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {configs.map((config) => (
                <li key={config.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          {config.name}
                        </h3>
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {getStoreTypeIcon(config.storeType)}
                          {getStoreTypeLabel(config.storeType)}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            config.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:gap-x-6 gap-y-1">
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Globe className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          <a
                            href={config.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-indigo-600 dark:hover:text-indigo-400"
                          >
                            {config.websiteUrl}
                          </a>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Clock className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          Daily at {config.scheduleTimes?.join(', ') || '08:00'}
                        </div>
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Mail className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          {config.recipients.length} recipient{config.recipients.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {config.lastRunAt && (
                        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                          Last run: {formatDateTime(config.lastRunAt)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => config.id && runReportMutation.mutate(config.id)}
                        disabled={runReportMutation.isPending}
                        className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                        title="Run Report Now"
                      >
                        <Play className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(config)}
                        className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {deleteConfirm === config.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => config.id && deleteMutation.mutate(config.id)}
                            className="inline-flex items-center p-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100"
                            title="Confirm Delete"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(config.id || null)}
                          className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={closeModal}
              ></div>

              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {editingConfig ? 'Edit Store Configuration' : 'Add Store Configuration'}
                    </h3>
                  </div>

                  <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                    {/* Store Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Store Type *
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {STORE_TYPES.map((type) => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, storeType: type.value }))}
                            className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
                              formData.storeType === type.value
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <span className="text-xs font-bold mb-1">{type.icon}</span>
                            <span className="text-sm">{type.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Store Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., CalTrend Main Store"
                      />
                    </div>

                    {/* Website URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Website URL *
                      </label>
                      <input
                        type="url"
                        required
                        value={formData.websiteUrl}
                        onChange={(e) => setFormData((prev) => ({ ...prev, websiteUrl: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                        placeholder="https://store.example.com"
                      />
                    </div>

                    {/* WooCommerce Credentials */}
                    {formData.storeType === 'woocommerce' && (
                      <>
                        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                          <p className="text-xs text-purple-700 dark:text-purple-300 mb-2">
                            Generate API keys in WooCommerce: Settings &gt; Advanced &gt; REST API
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Consumer Key *
                          </label>
                          <input
                            type="text"
                            value={formData.wooConsumerKey}
                            onChange={(e) => setFormData((prev) => ({ ...prev, wooConsumerKey: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Consumer Secret *
                          </label>
                          <input
                            type="password"
                            value={formData.wooConsumerSecret}
                            onChange={(e) => setFormData((prev) => ({ ...prev, wooConsumerSecret: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          />
                        </div>
                      </>
                    )}

                    {/* Magento 2 Credentials */}
                    {formData.storeType === 'magento2' && (
                      <>
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                          <p className="text-xs text-orange-700 dark:text-orange-300">
                            Create an Integration in Magento Admin: System &gt; Integrations &gt; Add New Integration.
                            Copy the Access Token after activation. Ensure "Allow OAuth Access Tokens to be used as standalone Bearer tokens" is set to Yes in Stores &gt; Configuration &gt; Services &gt; OAuth.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Access Token (Bearer Token) *
                          </label>
                          <input
                            type="password"
                            value={formData.magentoAccessToken}
                            onChange={(e) => setFormData((prev) => ({ ...prev, magentoAccessToken: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="Access Token from Magento Integration"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Store ID (optional)
                          </label>
                          <input
                            type="text"
                            value={formData.magentoStoreId}
                            onChange={(e) => setFormData((prev) => ({ ...prev, magentoStoreId: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="e.g., 1, 2, 3"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Leave empty for default store. For multi-store setups, enter the numeric store ID.
                          </p>
                        </div>
                      </>
                    )}

                    {/* Custom API Credentials */}
                    {formData.storeType === 'custom' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            API Endpoint *
                          </label>
                          <input
                            type="url"
                            value={formData.customApiEndpoint}
                            onChange={(e) => setFormData((prev) => ({ ...prev, customApiEndpoint: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                            placeholder="https://api.example.com/sales/daily"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            API should return: {`{ totalOrders: number, totalAmount: number }`}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            API Key (Bearer Token)
                          </label>
                          <input
                            type="password"
                            value={formData.customApiKey}
                            onChange={(e) => setFormData((prev) => ({ ...prev, customApiKey: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                            placeholder="Optional API key for authentication"
                          />
                        </div>
                      </>
                    )}

                    {/* Test Connection Button */}
                    <div>
                      <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={testConnectionMutation.isPending || !formData.websiteUrl}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
                      </button>
                    </div>

                    {/* Schedule Times */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Schedule Times * (up to 3)
                      </label>
                      <div className="space-y-2">
                        {formData.scheduleTimes.map((time, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="time"
                              required
                              value={time}
                              onChange={(e) => {
                                const newTimes = [...formData.scheduleTimes];
                                newTimes[index] = e.target.value;
                                setFormData((prev) => ({ ...prev, scheduleTimes: newTimes }));
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                            />
                            {formData.scheduleTimes.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    scheduleTimes: prev.scheduleTimes.filter((_, i) => i !== index),
                                  }));
                                }}
                                className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {formData.scheduleTimes.length < 3 && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              scheduleTimes: [...prev.scheduleTimes, '12:00'],
                            }));
                          }}
                          className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                        >
                          + Add another time
                        </button>
                      )}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Report will run daily at these times (server timezone)
                      </p>
                    </div>

                    {/* Recipients */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email Recipients *
                      </label>
                      <div className="space-y-2">
                        {formData.recipients.map((email, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => updateRecipient(index, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                              placeholder="email@example.com"
                            />
                            {formData.recipients.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRecipient(index)}
                                className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addRecipient}
                        className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                      >
                        + Add another recipient
                      </button>
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Active (report will run on schedule)
                      </label>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? 'Saving...'
                        : editingConfig
                        ? 'Update'
                        : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default WebsiteSalesReports;
